/**
 * Global Error Boundary with Sentry Integration
 * 
 * Catches React errors and displays user-friendly fallback UI.
 * Automatically reports errors to Sentry for debugging.
 * 
 * Styled to match Mellow's minimal, paper-like aesthetic.
 */

import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';

/**
 * Fallback UI shown when an error occurs
 */
function ErrorFallback({ error, resetError }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopyError = () => {
    const payload = JSON.stringify({
      name: error?.name,
      message: error?.message,
      stack: error?.stack,
    }, null, 2);
    navigator.clipboard.writeText(payload).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      backgroundColor: 'var(--color-bg)',
      fontFamily: 'var(--font-sans)',
    }}>
      {/* Error Icon */}
      <div style={{
        fontSize: '64px',
        marginBottom: '24px',
        opacity: 0.5,
      }}>
        😔
      </div>

      {/* Error Title */}
      <h1 style={{
        fontSize: '24px',
        fontWeight: '700',
        color: 'var(--color-text-main)',
        marginBottom: '12px',
        textAlign: 'center',
      }}>
        Something went wrong
      </h1>

      {/* Error Message */}
      <p style={{
        fontSize: '16px',
        color: 'var(--color-text-sub)',
        marginBottom: '32px',
        textAlign: 'center',
        maxWidth: '400px',
        lineHeight: '1.5',
      }}>
        We've been notified and will fix this soon. Try reloading the page or going back home.
      </p>

      {/* Error Details (dev only) */}
      {import.meta.env.DEV && error && (
        <details style={{
          marginBottom: '32px',
          padding: '16px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          maxWidth: '600px',
          width: '100%',
        }}>
          <summary style={{
            cursor: 'pointer',
            fontWeight: '600',
            marginBottom: '8px',
          }}>
            Error Details (Dev Only)
          </summary>
          <pre style={{
            fontSize: '12px',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>
            {error.toString()}
            {error.stack && `\n\n${error.stack}`}
          </pre>
        </details>
      )}

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        justifyContent: 'center',
      }}>
        <button
          onClick={handleReload}
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            color: '#fff',
            backgroundColor: 'var(--color-primary)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={(e) => e.target.style.opacity = '0.9'}
          onMouseLeave={(e) => e.target.style.opacity = '1'}
        >
          Reload Page
        </button>

        <button
          onClick={handleGoHome}
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            color: 'var(--color-text-main)',
            backgroundColor: 'transparent',
            border: '2px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--color-surface-hover)'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        >
          Go Home
        </button>
      </div>

      {/* Copy error as JSON */}
      {error && (
        <button
          onClick={handleCopyError}
          style={{
            marginTop: '16px',
            background: 'none',
            border: 'none',
            padding: '4px 8px',
            fontSize: '12px',
            color: copied ? 'var(--color-primary)' : 'var(--color-text-sub)',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontFamily: 'var(--font-sans)',
          }}
        >
          {copied ? 'Copied!' : 'Copy error as JSON'}
        </button>
      )}
    </div>
  );
}

/**
 * Error Boundary Component
 * Wraps the app to catch and handle React errors
 */
export default function ErrorBoundary({ children }) {
  return (
    <Sentry.ErrorBoundary
      fallback={ErrorFallback}
      showDialog={false} // Don't show Sentry dialog (we have custom UI)
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

